import { apiClient } from '@/api/client'
import type {
  CreateReceiptParams,
  GetReceiptsQuery,
  PriceSummary,
  PriceSummaryQuery,
  ReceiptDetail,
  ReceiptItem,
  ReceiptItemSearchResult,
  ReceiptListSummary,
  ReceiptPlaceCandidate,
  ReceiptSummary,
  ReceiptVisit,
  SearchReceiptItemsQuery,
  UpdateReceiptItemParams,
  UpdateReceiptParams,
} from '@/api/types/receipt'

/**
 * FastAPI の list クエリは `?prefecture=a&prefecture=b` 形式で受け取る。
 * axios のデフォルト（`prefecture[]=a`）では届かないので自前で組み立てる。
 */
const toSearchParams = (params: GetReceiptsQuery): string => {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      for (const item of value) search.append(key, String(item))
      continue
    }
    search.set(key, String(value))
  }
  return search.toString()
}

export const getReceipts = async (params?: GetReceiptsQuery, signal?: AbortSignal): Promise<ReceiptSummary[]> => {
  const query = toSearchParams(params ?? {})
  const res = await apiClient.get(query ? `/receipts?${query}` : '/receipts', { signal })
  return res.data
}

/** 一覧の合計件数・合計金額と、絞り込みの選択肢に添える件数。 */
export const getReceiptsSummary = async (
  params?: GetReceiptsQuery,
  signal?: AbortSignal,
): Promise<ReceiptListSummary> => {
  const query = toSearchParams(params ?? {})
  const res = await apiClient.get(query ? `/receipts/summary?${query}` : '/receipts/summary', { signal })
  return res.data
}

export const getReceiptById = async (receiptId: number, signal?: AbortSignal): Promise<ReceiptDetail> => {
  const res = await apiClient.get(`/receipts/${receiptId}`, { signal })
  return res.data
}

export const updateReceipt = async (receiptId: number, params: UpdateReceiptParams): Promise<ReceiptDetail> => {
  const res = await apiClient.patch(`/receipts/${receiptId}`, params)
  return res.data
}

/** レシートをもらえなかった買い物を手で登録する。 */
export const createReceipt = async (params: CreateReceiptParams): Promise<ReceiptDetail> => {
  const res = await apiClient.post('/receipts', params)
  return res.data
}

/** 手で登録したレシートを消す。PDF から取り込んだものは消せない。 */
export const deleteReceipt = async (receiptId: number): Promise<void> => {
  await apiClient.delete(`/receipts/${receiptId}`)
}

export const updateReceiptItem = async (
  receiptId: number,
  itemId: number,
  params: UpdateReceiptItemParams,
): Promise<ReceiptItem> => {
  const res = await apiClient.patch(`/receipts/${receiptId}/items/${itemId}`, params)
  return res.data
}

export const confirmReceipt = async (receiptId: number): Promise<ReceiptDetail> => {
  const res = await apiClient.post(`/receipts/${receiptId}/confirm`)
  return res.data
}

/** 場所を確定する。placeId を渡すと選び直したうえで確定する。 */
export const confirmReceiptPlace = async (receiptId: number, placeId?: number): Promise<ReceiptDetail> => {
  const res = await apiClient.post(`/receipts/${receiptId}/confirm-place`, null, {
    params: placeId === undefined ? undefined : { place_id: placeId },
  })
  return res.data
}

/** 場所の確定を取り消して、自動紐付けの対象に戻す。 */
export const unconfirmReceiptPlace = async (receiptId: number): Promise<ReceiptDetail> => {
  const res = await apiClient.delete(`/receipts/${receiptId}/confirm-place`)
  return res.data
}

/** 購入時刻の周りに居た滞在を候補として取得する。 */
export const getReceiptVisitCandidates = async (receiptId: number, signal?: AbortSignal): Promise<ReceiptVisit[]> => {
  const res = await apiClient.get(`/receipts/${receiptId}/visit-candidates`, { signal })
  return res.data
}

/** 選んだ滞在に紐付けて、場所も確定する。 */
export const confirmReceiptVisit = async (receiptId: number, visitId: number): Promise<ReceiptDetail> => {
  const res = await apiClient.post(`/receipts/${receiptId}/confirm-visit`, null, { params: { visit_id: visitId } })
  return res.data
}

/** Google Places を引いて、この店の候補を取得する。q 省略時は店名と住所から組み立てる。 */
export const getReceiptPlaceCandidates = async (
  receiptId: number,
  q?: string,
  signal?: AbortSignal,
): Promise<ReceiptPlaceCandidate[]> => {
  const res = await apiClient.get(`/receipts/${receiptId}/place-candidates`, { params: { q }, signal })
  return res.data
}

/** 検索候補から場所を決めて確定する。対応する Place が無ければサーバ側で作られる。 */
export const setReceiptPlace = async (receiptId: number, candidate: ReceiptPlaceCandidate): Promise<ReceiptDetail> => {
  const res = await apiClient.post(`/receipts/${receiptId}/place`, candidate)
  return res.data
}

/** 元のPDFを Blob URL として取得する。呼び出し側で revokeObjectURL すること。 */
export const fetchReceiptFileUrl = async (receiptId: number, signal?: AbortSignal): Promise<string> => {
  const res = await apiClient.get(`/receipts/${receiptId}/file`, { responseType: 'blob', signal })
  return URL.createObjectURL(res.data)
}

/** 買ったものをレシートを跨いで検索する。 */
export const searchReceiptItems = async (
  params: SearchReceiptItemsQuery,
  signal?: AbortSignal,
): Promise<ReceiptItemSearchResult[]> => {
  const res = await apiClient.get('/receipt-items', { params, signal })
  return res.data
}

/** 同じ商品を店ごとの単価にまとめて、安い順に取得する。 */
export const getPriceSummary = async (params: PriceSummaryQuery, signal?: AbortSignal): Promise<PriceSummary> => {
  const res = await apiClient.get('/receipt-items/price-summary', { params, signal })
  return res.data
}

/** 明細に実際に使われているカテゴリを取得する。 */
export const getReceiptItemCategories = async (signal?: AbortSignal): Promise<string[]> => {
  const res = await apiClient.get('/receipt-items/categories', { signal })
  return res.data
}
